import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import AuthenticatedNavigation from '../../components/navigation/AuthenticatedNavigation';
import ConversationList from './components/ConversationList';
import ChatWindow from './components/ChatWindow';
import InterviewScheduler from './components/InterviewScheduler';
import Icon from '../../components/AppIcon';
import { conversationsService, messagesService } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';

const SecureChatInterface = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showScheduler, setShowScheduler] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Mock conversations data
  const mockConversations = [
    {
      id: 1,
      matchId: 1,
      title: "Senior Engineering Leadership Role",
      companyInfo: "Technology Company • 500+ employees",
      lastMessage: "Looking forward to our discussion about the technical architecture.",
      lastMessageTime: new Date(Date.now() - 3600000),
      unreadCount: 2,
      isOnline: true,
      matchStage: "Reveal Approved",
      industry: "Technology"
    },
    {
      id: 2,
      matchId: 2,
      title: "VP of Product Development",
      companyInfo: "SaaS Company • 200-500 employees",
      lastMessage: "Your experience with product-market fit aligns perfectly with our needs.",
      lastMessageTime: new Date(Date.now() - 7200000),
      unreadCount: 0,
      isOnline: false,
      matchStage: "In Conversation",
      industry: "Technology"
    },
    {
      id: 3,
      matchId: 3,
      title: "Construction Project Director",
      companyInfo: "Construction Firm • 150-200 employees",
      lastMessage: "Can you share more about your approach to risk management?",
      lastMessageTime: new Date(Date.now() - 86400000),
      unreadCount: 1,
      isOnline: true,
      matchStage: "In Conversation",
      industry: "Construction"
    },
    {
      id: 4,
      matchId: 4,
      title: "Real Estate Development Lead",
      companyInfo: "Real Estate Company • 500+ employees",
      lastMessage: "I\'ve submitted the reveal request. Looking forward to discussing further.",
      lastMessageTime: new Date(Date.now() - 172800000),
      unreadCount: 0,
      isOnline: false,
      matchStage: "Reveal Pending",
      industry: "Real Estate"
    },
    {
      id: 5,
      matchId: 5,
      title: "Chief Technology Officer",
      companyInfo: "Enterprise Organization • 500+ employees",
      lastMessage: "Thank you for connecting. Let\'s discuss the technology vision.",
      lastMessageTime: new Date(Date.now() - 259200000),
      unreadCount: 0,
      isOnline: false,
      matchStage: "Initial Connection",
      industry: "Technology"
    }
  ];

  // Load conversations from Supabase
  useEffect(() => {
    const loadConversations = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await conversationsService?.getByUserId(user?.id);
        setConversations(data);
        if (data?.length > 0) {
          setSelectedConversation(data?.[0]);
        }
      } catch (err) {
        setError(err?.message);
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [user]);

  // Load messages for selected conversation
  useEffect(() => {
    const loadMessages = async () => {
      if (!selectedConversation) return;

      try {
        const data = await messagesService?.getByConversationId(selectedConversation?.id);
        setMessages(data);
      } catch (err) {
        console.error('Error loading messages:', err);
      }
    };

    loadMessages();
  }, [selectedConversation]);

  // Real-time message subscription
  useEffect(() => {
    if (!selectedConversation) return;

    const channel = supabase?.channel(`messages:${selectedConversation?.id}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${selectedConversation?.id}`
        },
        (payload) => {
          setMessages((prev) => [...prev, {
            id: payload?.new?.id,
            conversationId: payload?.new?.conversation_id,
            senderId: payload?.new?.sender_id,
            content: payload?.new?.content,
            status: payload?.new?.status,
            readAt: payload?.new?.read_at,
            createdAt: payload?.new?.created_at
          }]);
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [selectedConversation]);

  const filteredConversations = useMemo(() => {
    if (!searchQuery?.trim()) return conversations;
    
    const query = searchQuery?.toLowerCase();
    return conversations?.filter(conv => 
      conv?.title?.toLowerCase()?.includes(query) ||
      conv?.companyInfo?.toLowerCase()?.includes(query) ||
      conv?.lastMessage?.toLowerCase()?.includes(query)
    );
  }, [searchQuery, conversations]);

  const handleScheduleInterview = () => {
    setShowScheduler(true);
  };

  const handleSchedulerClose = () => {
    setShowScheduler(false);
  };

  const handleSchedulerSubmit = (scheduleData) => {
    console.log('Interview scheduled:', scheduleData);
    setShowScheduler(false);
  };

  const handleSendMessage = async (content) => {
    if (!selectedConversation || !content?.trim()) return;

    try {
      await messagesService?.create({
        conversationId: selectedConversation?.id,
        content: content?.trim()
      });
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-white">
            <p>Loading conversations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AuthenticatedNavigation userRole="candidate" matchCount={0} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-400">
            <p>Error loading conversations: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Secure Chat - BlinD</title>
        <meta name="description" content="Secure encrypted communication with matched employers and candidates" />
      </Helmet>

      <div className="min-h-screen bg-[#F6F7FB]">
        <AuthenticatedNavigation userRole="candidate" matchCount={3} />

        <div className="max-w-[1920px] mx-auto p-4 md:p-6 lg:p-8 lg:pl-60 pb-20 lg:pb-8">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-semibold text-[#111827] mb-2">Secure Messages</h1>
            <p className="text-sm md:text-base text-[#6B7280]">
              End-to-end encrypted communication with your matched connections
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 h-[calc(100vh-240px)]">
            {/* Conversation List */}
            <div className="lg:col-span-4 xl:col-span-3">
              <ConversationList
                conversations={filteredConversations}
                selectedId={selectedConversation?.id}
                onSelect={setSelectedConversation}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />
            </div>

            {/* Chat Window */}
            <div className="lg:col-span-8 xl:col-span-9">
              {selectedConversation ? (
                <ChatWindow
                  conversation={selectedConversation}
                  onScheduleInterview={handleScheduleInterview}
                />
              ) : (
                <div className="bg-white rounded-2xl border border-[#E5E7EB] h-full flex flex-col items-center justify-center p-8">
                  <div className="w-20 h-20 rounded-xl bg-[#F6F7FB] border-2 border-[#E5E7EB] flex items-center justify-center mb-4">
                    <Icon name="MessageSquare" size={40} color="#6B7280" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#111827] mb-2">Select a conversation</h3>
                  <p className="text-sm text-[#6B7280] text-center max-w-md">
                    Choose a conversation from the list to start messaging with your matched connections
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Interview Scheduler Modal */}
        {showScheduler && selectedConversation && (
          <InterviewScheduler
            conversation={selectedConversation}
            onClose={handleSchedulerClose}
            onSubmit={handleSchedulerSubmit}
          />
        )}
      </div>
    </>
  );
};

export default SecureChatInterface;